import * as styles from "./ProfilerToolbar.css";

import type { Ref } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";

import { type Edge, nearestEdge } from "./useDraggablePosition";

type Point = { x: number; y: number };
type Size = { width: number; height: number };

// Breathing room between the folded pill and the screen edge
const INSET = 8;

// The pill folded: the same shape, smaller, with the duration alone, near
// the edge it was closest to and level with where it was. A click opens
// the popover like the pill, a double-click unfolds it, as one folded it.
export function ProfilerFolded({
  ref,
  position,
  size,
  tone,
  label,
  open,
  onClick,
  onExpand,
}: {
  ref: Ref<HTMLButtonElement>;
  position: Point;
  size: Size;
  tone?: "success" | "warning" | "error";
  label: string;
  open: boolean;
  onClick: () => void;
  onExpand: () => void;
}) {
  const edge = nearestEdge(position, size);
  const folded = styles.FOLDED_ESTIMATE;
  const centre = {
    x: position.x + size.width / 2 - folded.width / 2,
    y: position.y + size.height / 2 - folded.height / 2,
  };
  const placement: Record<Edge, { left: number; top: number }> = {
    left: { left: INSET, top: centre.y },
    right: { left: window.innerWidth - folded.width - INSET, top: centre.y },
    top: { left: centre.x, top: INSET },
    bottom: { left: centre.x, top: window.innerHeight - folded.height - INSET },
  };

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            ref={ref}
            type="button"
            className={styles.folded}
            style={placement[edge]}
            data-tone={tone}
            onClick={onClick}
            onDoubleClick={onExpand}
            aria-label="Profiler, folded"
            aria-expanded={open}
          />
        }
      >
        {label}
      </TooltipTrigger>
      <TooltipContent side={oppositeOf(edge)}>
        Profiler: click for the figures, double-click to unfold
      </TooltipContent>
    </Tooltip>
  );
}

function oppositeOf(edge: Edge): Edge {
  switch (edge) {
    case "left":
      return "right";
    case "right":
      return "left";
    case "top":
      return "bottom";
    default:
      return "top";
  }
}
